import React from 'react';
import { Form as FinalForm } from 'react-final-form';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import * as validators from '../../util/validators';
import {
  Form,
  PrimaryButton,
  FieldTextInput,
  FieldRichTextInput,
  Page,
  LayoutSingleColumn,
} from '../../components';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import endorcmentLogo from '../../assets/images/endorcmentLogo.png';

import css from './EndorcmentLandingPage.module.css';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import IconCollection from '../../components/IconCollection/IconCollection';

const EndorsementForm = () => {
  const intl = useIntl();

  const handleSubmit = values => {
    console.log('Form submitted:', values);
    // Handle form submission here
  };

  return (
    <FinalForm
      onSubmit={handleSubmit}
      render={({ handleSubmit, form, submitting, pristine, values }) => (
        <Form className={css.endorsementForm} onSubmit={handleSubmit}>
          <h2 className={css.formTitle}>
            <FormattedMessage id="EndorcmentLandingPage.formTitle" />
          </h2>

          <div className={css.formRow}>
            <div className={css.formField}>
              <label htmlFor="firstName">
                <FormattedMessage id="EndorcmentLandingPage.firstNameLabel" />
              </label>
              <FieldTextInput
                id="firstName"
                name="firstName"
                placeholder={intl.formatMessage({
                  id: 'EndorcmentLandingPage.firstNamePlaceholder',
                })}
                validate={validators.required(
                  intl.formatMessage({ id: 'EndorcmentLandingPage.firstNameRequired' })
                )}
              />
            </div>
            <div className={css.formField}>
              <label htmlFor="lastName">
                <FormattedMessage id="EndorcmentLandingPage.lastNameLabel" />
              </label>
              <FieldTextInput
                id="lastName"
                name="lastName"
                placeholder={intl.formatMessage({
                  id: 'EndorcmentLandingPage.lastNamePlaceholder',
                })}
                validate={validators.required(
                  intl.formatMessage({ id: 'EndorcmentLandingPage.lastNameRequired' })
                )}
              />
            </div>
          </div>

          <div className={css.formRow}>
            <div className={css.formField}>
              <label htmlFor="companyName">
                <FormattedMessage id="EndorcmentLandingPage.companyNameLabel" />
              </label>
              <FieldTextInput
                id="companyName"
                name="companyName"
                placeholder={intl.formatMessage({
                  id: 'EndorcmentLandingPage.companyNamePlaceholder',
                })}
                validate={validators.required(
                  intl.formatMessage({ id: 'EndorcmentLandingPage.companyNameRequired' })
                )}
              />
            </div>
            <div className={css.formField}>
              <label htmlFor="email">
                <FormattedMessage id="EndorcmentLandingPage.emailLabel" />
              </label>
              <FieldTextInput
                id="email"
                name="email"
                type="email"
                placeholder={intl.formatMessage({ id: 'EndorcmentLandingPage.emailPlaceholder' })}
                validate={validators.composeValidators(
                  validators.required(
                    intl.formatMessage({ id: 'EndorcmentLandingPage.emailRequired' })
                  ),
                  validators.emailFormatValid(
                    intl.formatMessage({ id: 'EndorcmentLandingPage.emailInvalid' })
                  )
                )}
              />
            </div>
          </div>

          <div className={css.formRow}>
            <div className={css.formFieldFull}>
              <label htmlFor="endorsement">
                <FormattedMessage id="EndorcmentLandingPage.endorsementLabel" />
              </label>
              <FieldTextInput
                id="endorsement"
                name="endorsement"
                type="textarea"
                placeholder={intl.formatMessage({
                  id: 'EndorcmentLandingPage.endorsementPlaceholder',
                })}
                validate={validators.required(
                  intl.formatMessage({ id: 'EndorcmentLandingPage.endorsementRequired' })
                )}
              />
            </div>
          </div>

          <p className={css.offerText}>
            <FormattedMessage
              id="EndorcmentLandingPage.offerText"
              values={{
                offerTextSpan: (
                  <span className={css.offerTextSpan}>
                    <FormattedMessage id="EndorcmentLandingPage.offerTextSpan" />
                  </span>
                ),
              }}
            />
          </p>

          <PrimaryButton
            type="submit"
            className={css.submitButton}
            disabled={submitting || pristine}
          >
            <FormattedMessage id="EndorcmentLandingPage.submitButton" />
          </PrimaryButton>
        </Form>
      )}
    />
  );
};

const EndorcmentLandingPage = ({ scrollingDisabled }) => {
  const scrollToForm = () => {
    const formSection = document.querySelector(`.${css.formSection}`);
    if (formSection) {
      formSection.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  const marketplaceName = 'ProProjectLink';
  const schemaTitle = 'Endorsement Landing Page';
  const schemaDescription = 'Complete your endorsement for ProProjectLink';

  return (
    <Page
      title={schemaTitle}
      scrollingDisabled={scrollingDisabled}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'WebPage',
        name: schemaTitle,
        description: schemaDescription,
      }}
    >
      <LayoutSingleColumn
        mainColumnClassName={css.layoutWrapperMain}
        topbar={<TopbarContainer />}
        footer={<FooterContainer />}
      >
        <div className={css.pageContainer}>
          {/* Hero Section */}
          <section className={css.heroSection}>
            <div className={css.heroBackground}>
              <div className={css.heroOverlay}>
                <div className={css.heroContent}>
                  <div className={css.badge}>
                    <img src={endorcmentLogo} alt="badge" />
                  </div>
                  <h1 className={css.heroTitle}>
                    <FormattedMessage id="EndorcmentLandingPage.heroTitle" />
                  </h1>
                  <p className={css.heroSubtitle}>
                    <FormattedMessage id="EndorcmentLandingPage.heroSubtitle" />
                  </p>
                  <div
                    className={css.scrollIndicator}
                    onClick={scrollToForm}
                    style={{ cursor: 'pointer' }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        scrollToForm();
                      }
                    }}
                    aria-label="Scroll to form section"
                  >
                    <div className={css.scrollArrow}>
                      <IconCollection name="arrow-down" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Industry Connections Section */}
          <section className={css.industrySection}>
            <div className={css.industryContent}>
              <h2 className={css.industryTitle}>
                <FormattedMessage id="EndorcmentLandingPage.industryTitle" />
              </h2>
              <p className={css.industryDescription}>
                <FormattedMessage id="EndorcmentLandingPage.industryDescription1" />
              </p>
              <p className={css.industryDescription}>
                <FormattedMessage id="EndorcmentLandingPage.industryDescription2" />
              </p>
            </div>
          </section>

          {/* Value Proposition Section */}
          <section className={css.valueSection}>
            <div className={css.valueContent}>
              <h2 className={css.valueTitle}>
                <FormattedMessage id="EndorcmentLandingPage.valueTitle" />
              </h2>
              <div className={css.valueGrid}>
                <div className={css.valueColumn}>
                  <h3 className={css.valueColumnTitle}>
                    <FormattedMessage id="EndorcmentLandingPage.valueColumnTitle" />
                  </h3>
                  <p className={css.valueColumnText}>
                    <FormattedMessage id="EndorcmentLandingPage.valueColumnText" />
                  </p>
                </div>
                <div className={css.valueColumn}>
                  <h3 className={css.valueColumnTitle}>
                    <FormattedMessage id="EndorcmentLandingPage.valueColumnTitle" />
                  </h3>
                  <p className={css.valueColumnText}>
                    <FormattedMessage id="EndorcmentLandingPage.valueColumnText" />
                  </p>
                </div>
                <div className={css.valueColumn}>
                  <h3 className={css.valueColumnTitle}>
                    <FormattedMessage id="EndorcmentLandingPage.valueColumnTitle" />
                  </h3>
                  <p className={css.valueColumnText}>
                    <FormattedMessage id="EndorcmentLandingPage.valueColumnText" />
                  </p>
                </div>
                <div className={css.valueColumn}>
                  <h3 className={css.valueColumnTitle}>
                    <FormattedMessage id="EndorcmentLandingPage.valueColumnTitle" />
                  </h3>
                  <p className={css.valueColumnText}>
                    <FormattedMessage id="EndorcmentLandingPage.valueColumnText" />
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Endorsement Form Section */}
          <section className={css.formSection}>
            <div className={css.formBackground}>
              <div className={css.formContainer}>
                <EndorsementForm />
              </div>
            </div>
          </section>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => {
  return {
    scrollingDisabled: isScrollingDisabled(state),
  };
};

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
const EndorcmentLandingPageContainer = compose(connect(mapStateToProps))(EndorcmentLandingPage);

export default EndorcmentLandingPageContainer;
